import React, { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import Button from 'components/base/Button';
import { FolderSelect } from 'components/forms/FolderSelect';
import { EditSchedulingWindowIdReference } from 'edit/reference/EditSchedulingWindowIdReference';
import QueueAllocationStatusesField from './QueueAllocationStatusesField';
import type { Worker } from '../../../../redux/slices/connectionsSlice';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';

export interface EditQueueFormState {
  queue_id: number;
  queue_name: string;
  queue_new_name: string;
  queue_limit: number;
  queue_status: string;
  zone_name: string;
  zone_new_name: string;
  queue_description: string;
  workers: { worker_id: number; worker_name: string; priority: number }[];
  connections: { connection_id: number; connection_name: string }[];
  scheduling_window_id: number | null;
  queue_allocation_statuses: string[];
  folder_id: number | null;
}

type Props = {
  show: boolean;
  value: EditQueueFormState | null;

  zones: { id: string; name: string }[];
  connections: any[];
  workers: Worker[];

  allocationStatusOptions: string[];

  onChange: (next: EditQueueFormState | null) => void;
  onClose: () => void;
  onSave: (form: EditQueueFormState) => void | Promise<void>;
};

const EditQueueModal: React.FC<Props> = ({ show, value, zones, connections, workers, allocationStatusOptions, onChange, onClose, onSave }) => {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const isDirty = useUnsavedChanges(show, value ?? {});

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  if (!value) return null;

  const effectiveEditZone = value.zone_new_name || value.zone_name;

  const setField = (name: string, v: any) => onChange({ ...value, [name]: v });

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value: v } = e.target;

    const parsed = name === 'queue_limit' ? Number(v) || 0 : v;
    const next: EditQueueFormState = { ...value, [name]: parsed };

    // If zone changes, reset schedule and folder to avoid cross-zone mismatch
    if (name === 'zone_new_name') {
      next.scheduling_window_id = null;
      next.folder_id = null;
    }

    onChange(next);
  };

  const handleConnectionSelection = (connection: any, checked: boolean) => {
    const list = Array.isArray(value.connections) ? value.connections : [];
    const updated = checked
      ? [
          ...list,
          {
            connection_id: connection.connection_id,
            connection_name: connection.connection_name,
          },
        ]
      : list.filter((c: any) => c.connection_id !== connection.connection_id);

    setField('connections', updated);
  };

  const handleWorkerSelection = (worker: Worker, checked: boolean) => {
    const list = Array.isArray(value.workers) ? value.workers : [];
    const updated = checked
      ? [
          ...list,
          {
            worker_id: worker.worker_id,
            worker_name: worker.worker_name,
            priority: 0,
          },
        ]
      : list.filter((w: any) => w.worker_id !== worker.worker_id);

    setField('workers', updated);
  };

  const handleWorkerPriorityChange = (worker_id: number, priority: number) => {
    setField(
      'workers',
      (value.workers || []).map((w: any) => (w.worker_id === worker_id ? { ...w, priority } : w))
    );
  };

  return (
    <Modal show={show} onHide={handleRequestClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Queue</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Queue Name</Form.Label>
            <Form.Control type="text" name="queue_new_name" value={value.queue_new_name || value.queue_name} onChange={handleChange} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Queue Status</Form.Label>
            <Form.Select name="queue_status" value={value.queue_status} onChange={handleChange}>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Zone</Form.Label>
            <Form.Select name="zone_new_name" value={value.zone_new_name || value.zone_name} onChange={handleChange}>
              <option value="">Select a Zone</option>
              {zones.map((z) => (
                <option key={z.id} value={z.name}>
                  {z.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Folder</Form.Label>
            <FolderSelect value={value.folder_id} onChange={(folder_id) => setField('folder_id', folder_id)} zoneName={effectiveEditZone} />
          </Form.Group>

          <Form.Group className="mb-3" controlId="scheduling_window_id">
            <Form.Label>Schedule Window</Form.Label>
            <EditSchedulingWindowIdReference
              placeholder="No schedule window"
              zoneName={effectiveEditZone || undefined}
              value={value.scheduling_window_id}
              onChange={(id) => setField('scheduling_window_id', id)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Queue Limit</Form.Label>
            <Form.Control type="number" name="queue_limit" value={value.queue_limit} onChange={handleChange} />
            <Form.Text className="text-muted">Enter 0 for no limit</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} name="queue_description" value={value.queue_description} onChange={handleChange} />
          </Form.Group>

          <QueueAllocationStatusesField
            options={allocationStatusOptions}
            value={value.queue_allocation_statuses}
            onChange={(next) => setField('queue_allocation_statuses', next)}
            tooltipId="edit-allocation-statuses-tooltip"
          />

          <Form.Group className="mb-3">
            <Form.Label>Connections</Form.Label>
            <div className="overflow-auto border rounded p-2" style={{ maxHeight: 260 }}>
              {[...connections]
                .sort((a, b) => (a.connection_name || '').localeCompare(b.connection_name || ''))
                .map((c: any) => {
                  const isChecked = Array.isArray(value.connections) && value.connections.some((x: any) => x.connection_id === c.connection_id);

                  return (
                    <Form.Check
                      key={c.connection_id}
                      type="checkbox"
                      className="mb-1"
                      label={`${c.connection_name}${c.connection_type ? ` (${c.connection_type})` : ''}`}
                      checked={!!isChecked}
                      onChange={(e) => handleConnectionSelection(c, e.target.checked)}
                    />
                  );
                })}
            </div>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Workers</Form.Label>
            {workers.map((w: Worker) => {
              const checked = Array.isArray(value.workers) ? value.workers.some((x: any) => x.worker_id === w.worker_id) : false;

              const current = Array.isArray(value.workers) ? value.workers.find((x: any) => x.worker_id === w.worker_id) : undefined;

              return (
                <div key={w.worker_id} className="mb-2">
                  <Form.Check type="checkbox" label={w.worker_name} checked={!!checked} onChange={(e) => handleWorkerSelection(w, e.target.checked)} />
                  {checked && (
                    <Form.Control
                      type="number"
                      className="mt-1"
                      value={current?.priority ?? 0}
                      onChange={(e) => handleWorkerPriorityChange(w.worker_id, parseInt(e.target.value || '0', 10))}
                      placeholder="Priority"
                    />
                  )}
                </div>
              );
            })}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleRequestClose}>
          Close
        </Button>
        <Button variant="primary" onClick={() => onSave(value)}>
          Save and Close
        </Button>
      </Modal.Footer>
      <ConfirmationModal
        show={showCloseConfirm}
        title="Unsaved Changes"
        body="You have unsaved changes. Are you sure you want to close without saving?"
        confirmText="Discard Changes"
        confirmVariant="danger"
        onConfirm={() => {
          setShowCloseConfirm(false);
          onClose();
        }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </Modal>
  );
};

export default EditQueueModal;
